/**
 * IntroPage — Dukkan 3D Gallery
 * Snap navigation: one scroll/swipe = one room.
 * Image fills the full viewport. Cartier-style concentric frame rings.
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import * as THREE from 'three'
import './IntroPage.css'

// ── Constants ─────────────────────────────────────────────────
const N            = 6
const FOV          = 62
const ROOM_SPACING = 30
const VIEW_DIST    = 11
const IMG_Z_LOCAL  = -10

// Exact viewport-filling dimensions at VIEW_DIST, then camera.zoom handles the rest.
const IS_MOBILE   = window.innerWidth < 768
const _visibleH   = 2 * VIEW_DIST * Math.tan((FOV / 2) * (Math.PI / 180))
const _viewAspect = window.innerWidth / window.innerHeight
const IMG_H       = _visibleH        // exact screen height in world units
const IMG_W       = IMG_H * _viewAspect  // exact screen width in world units

const camZForRoom = (i: number) => -i * ROOM_SPACING + IMG_Z_LOCAL + VIEW_DIST

// ── Scene data ────────────────────────────────────────────────
interface StoreScene {
  id: string; name: string; tagline: string; description: string
  image: string; accentColor: string; accentHex: number; emoji: string
}

const SCENES: StoreScene[] = [
  { id: 'cafe', name: 'Café', emoji: '☕',
    tagline: 'Every corner, your seat',
    description: 'Step inside a real café in 3D — pick your spot before you arrive.',
    image: '/intro/cafe.png', accentColor: '#d97706', accentHex: 0xd97706 },
  { id: 'electronics', name: 'Electronics', emoji: '📱',
    tagline: 'Every shelf, in your hand',
    description: 'Browse the full store in 3D — compare, decide, then walk in ready.',
    image: '/intro/electronics.png', accentColor: '#3b82f6', accentHex: 0x3b82f6 },
  { id: 'clothing', name: 'Fashion', emoji: '👗',
    tagline: 'Every rack, already yours',
    description: 'Walk the whole boutique in 3D — feel the space before the visit.',
    image: '/intro/clothing.png', accentColor: '#ec4899', accentHex: 0xec4899 },
  { id: 'supermarket', name: 'Supermarket', emoji: '🛒',
    tagline: 'Every aisle, explored',
    description: 'Navigate a full supermarket in 3D — plan your trip, skip the queue.',
    image: '/intro/supermarket.png', accentColor: '#22c55e', accentHex: 0x22c55e },
  { id: 'bookshop', name: 'Bookshop', emoji: '📚',
    tagline: 'Every shelf, within reach',
    description: 'Wander a real bookshop in 3D — browse like you’re already inside.',
    image: '/intro/bookshop.png', accentColor: '#a78bfa', accentHex: 0xa78bfa },
  { id: 'pharmacy', name: 'Pharmacy', emoji: '💊',
    tagline: 'Every shelf, found fast',
    description: 'Walk the pharmacy floor in 3D — know where to go before you go.',
    image: '/intro/pharmacy.png', accentColor: '#06b6d4', accentHex: 0x06b6d4 },
]

// ── Room builder ──────────────────────────────────────────────
interface RoomResult { group: THREE.Group; imgMat: THREE.MeshBasicMaterial }

function buildRoom(s: StoreScene, tex: THREE.Texture): RoomResult {
  const group  = new THREE.Group()
  const accent = new THREE.Color(s.accentHex)

  // Full-screen image (MeshBasicMaterial: true colors, unaffected by lights)
  const imgMat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: 1 })
  const img    = new THREE.Mesh(new THREE.PlaneGeometry(IMG_W, IMG_H), imgMat)
  img.position.set(0, 0, IMG_Z_LOCAL)
  group.add(img)

  // Concentric Cartier-style glowing rings receding in depth
  const RINGS = 10
  for (let r = 0; r < RINGS; r++) {
    const t      = r / (RINGS - 1)
    const scale  = 1.03 + r * 0.10
    const rW     = IMG_W * scale
    const rH     = IMG_H * scale
    const zPos   = IMG_Z_LOCAL + 0.25 + r * 1.1
    const opa    = 0.9 - t * 0.75
    const thick  = 0.022

    const mat = new THREE.MeshBasicMaterial({
      color: accent, transparent: true, opacity: opa,
      depthWrite: false, blending: THREE.AdditiveBlending,
    })

    const bar = (bw: number, bh: number, bx: number, by: number) => {
      const m = new THREE.Mesh(new THREE.PlaneGeometry(bw, bh), mat)
      m.position.set(bx, by, zPos)
      return m
    }
    group.add(
      bar(rW + thick * 2, thick,  0,                  rH / 2 + thick / 2),
      bar(rW + thick * 2, thick,  0,                 -rH / 2 - thick / 2),
      bar(thick,          rH,    -rW / 2 - thick / 2, 0),
      bar(thick,          rH,     rW / 2 + thick / 2, 0),
    )
  }

  return { group, imgMat }
}

// ── Component ─────────────────────────────────────────────────
export default function IntroPage() {
  const navigate  = useNavigate()
  const mountRef  = useRef<HTMLDivElement>(null)
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null)

  const [activeScene, setActiveScene] = useState(0)
  const [showSkip,    setShowSkip]    = useState(false)
  const [showFinal,   setShowFinal]   = useState(false)

  // Continuous scroll model — `scrollTarget` is a fractional room index (0..N-1).
  // The render loop lerps `scrollCurrent` toward it every frame, so camera Z is
  // always smoothly driven by accumulated input. No GSAP timelines, no wheel lock.
  const scrollTarget  = useRef(0)
  const scrollCurrent = useRef(0)
  const velocity      = useRef(0)         // d(scrollCurrent)/frame, smoothed
  const mouse         = useRef({ x: 0, y: 0 })
  const idleTimer     = useRef<number | null>(null)
  const groupsRef     = useRef<THREE.Group[]>([])
  const rendRef       = useRef<{ renderer: THREE.WebGLRenderer; animId: number } | null>(null)

  // Set scroll target (clamped). Active scene state updates from the render loop.
  const setTarget = useCallback((t: number) => {
    scrollTarget.current = Math.max(0, Math.min(N - 1, t))
  }, [])

  // Public "snap to room N" — used by dot clicks and keyboard.
  const snapToRoom = useCallback((idx: number) => {
    setTarget(idx)
    if (idleTimer.current) { window.clearTimeout(idleTimer.current); idleTimer.current = null }
  }, [setTarget])

  // After scroll input goes quiet, gently round to the nearest room.
  const scheduleIdleSnap = useCallback(() => {
    if (idleTimer.current) window.clearTimeout(idleTimer.current)
    idleTimer.current = window.setTimeout(() => {
      scrollTarget.current = Math.round(scrollTarget.current)
    }, 220)
  }, [])

  // ── Three.js setup ─────────────────────────────────────────
  useEffect(() => {
    const mount = mountRef.current
    if (!mount) return

    const renderer = new THREE.WebGLRenderer({ antialias: true })
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.setSize(window.innerWidth, window.innerHeight)
    renderer.toneMapping         = THREE.ACESFilmicToneMapping
    renderer.toneMappingExposure = 0.88
    renderer.outputColorSpace    = THREE.SRGBColorSpace
    mount.appendChild(renderer.domElement)

    const scene = new THREE.Scene()
    scene.background = new THREE.Color(0x040208)
    scene.fog        = new THREE.FogExp2(0x040208, 0.016)

    const camera = new THREE.PerspectiveCamera(FOV, window.innerWidth / window.innerHeight, 0.1, 300)
    camera.position.set(0, 0, camZForRoom(0))
    // zoom > 1 shrinks the frustum → image appears larger → guaranteed full-bleed
    camera.zoom = IS_MOBILE ? 1.45 : 1.15
    camera.updateProjectionMatrix()
    cameraRef.current = camera

    scene.add(new THREE.AmbientLight(0x0a0614, 2))

    // Minimal corridor — just dark walls, no arch beams
    const corrLen = N * ROOM_SPACING + 60
    const cz      = -(corrLen * 0.45)
    const CW = 6, CH_LO = -4.5, CH_HI = 5.0, wallH = CH_HI - CH_LO

    const flMat = new THREE.MeshStandardMaterial({ color: 0x080610, roughness: 0.05, metalness: 0.95 })
    const fl = new THREE.Mesh(new THREE.PlaneGeometry(CW * 2, corrLen), flMat)
    fl.rotation.x = -Math.PI / 2
    fl.position.set(0, CH_LO, cz)
    scene.add(fl)

    const ceMat = new THREE.MeshStandardMaterial({ color: 0x020106, roughness: 1 })
    const ce = new THREE.Mesh(new THREE.PlaneGeometry(CW * 2, corrLen), ceMat)
    ce.rotation.x = Math.PI / 2
    ce.position.set(0, CH_HI, cz)
    scene.add(ce)

    ;[-1, 1].forEach((s) => {
      const wm = new THREE.MeshStandardMaterial({ color: 0x0a0810, roughness: 0.92 })
      const w  = new THREE.Mesh(new THREE.PlaneGeometry(corrLen, wallH), wm)
      w.rotation.y = s > 0 ? Math.PI / 2 : -Math.PI / 2
      w.position.set(s * CW, (CH_HI + CH_LO) / 2, cz)
      scene.add(w)
    })

    // Build rooms
    const loader = new THREE.TextureLoader()
    const groups: THREE.Group[] = []

    SCENES.forEach((s, i) => {
      const src = IS_MOBILE ? s.image.replace('.png', '-mobile.png') : s.image
      const tex = loader.load(src, (t) => {
        t.colorSpace = THREE.SRGBColorSpace
        t.anisotropy = renderer.capabilities.getMaxAnisotropy()
      })
      const { group, imgMat } = buildRoom(s, tex)
      group.position.set(0, 0, -i * ROOM_SPACING)
      imgMat.opacity = 1
      scene.add(group)
      groups.push(group)
    })

    groupsRef.current = groups
    rendRef.current   = { renderer, animId: 0 }

    // Pre-compute dark-tinted accent colors for atmosphere blending.
    const accentDark = SCENES.map((s) => new THREE.Color(s.accentHex).multiplyScalar(0.045))
    const bgColor   = new THREE.Color()
    const fogColor  = (scene.fog as THREE.FogExp2).color
    const baseFov   = FOV
    const baseZoom  = IS_MOBILE ? 1.45 : 1.15

    // Render loop — continuous, frame-driven. No GSAP, no locks.
    let lastScroll = scrollCurrent.current
    let currentRoom = 0

    const animate = () => {
      rendRef.current!.animId = requestAnimationFrame(animate)
      const tt = clock.getElapsedTime()

      // 1. Smooth scroll: lerp current toward target every frame.
      scrollCurrent.current += (scrollTarget.current - scrollCurrent.current) * 0.085

      // 2. Velocity (smoothed) — drives all dynamic effects.
      const rawVel = scrollCurrent.current - lastScroll
      lastScroll = scrollCurrent.current
      velocity.current += (rawVel - velocity.current) * 0.18
      const vAbs = Math.min(Math.abs(velocity.current) * 14, 1)   // 0..1

      // 3. Camera position from fractional scroll.
      camera.position.x = mouse.current.x * 0.45                  // parallax drift
      camera.position.y = mouse.current.y * 0.28 + Math.sin(tt * 0.22) * 0.04
      camera.position.z = camZForRoom(scrollCurrent.current)

      // 4. FOV / zoom pulse with velocity — speed widens the frame slightly.
      camera.fov  = baseFov + vAbs * 8
      camera.zoom = baseZoom - vAbs * 0.12
      camera.updateProjectionMatrix()

      // 5. lookAt drifts with mouse — true parallax, not just XY translate.
      const focusZ = -scrollCurrent.current * ROOM_SPACING + IMG_Z_LOCAL
      camera.lookAt(mouse.current.x * 0.9, mouse.current.y * 0.55, focusZ)

      // 6. Atmosphere blend — background & fog colors interpolate between
      //    adjacent rooms based on fractional scroll. Each room casts its
      //    accent into the corridor.
      const i0 = Math.max(0, Math.min(N - 2, Math.floor(scrollCurrent.current)))
      const ft = THREE.MathUtils.clamp(scrollCurrent.current - i0, 0, 1)
      bgColor.copy(accentDark[i0]).lerp(accentDark[i0 + 1], ft)
      ;(scene.background as THREE.Color).copy(bgColor)
      fogColor.copy(bgColor)

      // 7. Per-room tilt + velocity pulse. Mouse drives a calm parallax tilt;
      //    scroll velocity adds a directional roll and a brief scale pulse —
      //    the rooms feel like they're reacting to the camera's momentum.
      const tiltX = -mouse.current.y * 0.06
      const tiltY =  mouse.current.x * 0.06
      const rollZ =  velocity.current * 0.18                       // signed: leans into motion
      const pulse =  1 + vAbs * 0.045                              // up to +4.5% scale on fast scroll
      groups.forEach((g) => {
        g.rotation.x = tiltX
        g.rotation.y = tiltY
        g.rotation.z = rollZ
        g.scale.setScalar(pulse)
      })

      // 8. Brightness lift — tone-mapping exposure rises slightly while moving,
      //    so the scene feels energised when you're in motion and calm when still.
      renderer.toneMappingExposure = 0.88 + vAbs * 0.35

      // 9. Active-scene state for UI labels — derived from camera position.
      const nearest = Math.round(scrollCurrent.current)
      if (nearest !== currentRoom) {
        currentRoom = nearest
        setActiveScene(nearest)
        setShowFinal(nearest === N - 1)
      }

      renderer.render(scene, camera)
    }
    const clock = new THREE.Clock()
    animate()

    const onResize = () => {
      renderer.setSize(window.innerWidth, window.innerHeight)
      camera.aspect = window.innerWidth / window.innerHeight
      camera.zoom = window.innerWidth < 768 ? 1.45 : 1.15
      camera.updateProjectionMatrix()
    }
    window.addEventListener('resize', onResize)
    // Also fire on visualViewport resize (mobile browser chrome show/hide)
    window.visualViewport?.addEventListener('resize', onResize)

    return () => {
      window.removeEventListener('resize', onResize)
      window.visualViewport?.removeEventListener('resize', onResize)
      cancelAnimationFrame(rendRef.current!.animId)
      renderer.dispose()
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement)
    }
  }, [])

  // ── Input: wheel + touch + mouse + keyboard ───────────────
  useEffect(() => {
    // Per-pixel wheel sensitivity — one normal wheel notch ≈ 100px on most OSes,
    // so 1/900 gives ~1 room per ~9 notches before idle-snap completes the move.
    const WHEEL_SENS = 1 / 900
    const TOUCH_SENS = 1 / 220     // px → rooms; faster than wheel because fingers swipe further

    let touchY = 0, touchX = 0, touchActive = false

    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const d = Math.abs(e.deltaY) > Math.abs(e.deltaX) ? e.deltaY : e.deltaX
      setTarget(scrollTarget.current + d * WHEEL_SENS)
      scheduleIdleSnap()
    }

    const onTouchStart = (e: TouchEvent) => {
      touchY = e.touches[0].clientY
      touchX = e.touches[0].clientX
      touchActive = true
      if (idleTimer.current) { window.clearTimeout(idleTimer.current); idleTimer.current = null }
    }
    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault()
      if (!touchActive) return
      const ny = e.touches[0].clientY, nx = e.touches[0].clientX
      const dy = touchY - ny, dx = touchX - nx
      const d  = Math.abs(dy) >= Math.abs(dx) ? dy : dx
      setTarget(scrollTarget.current + d * TOUCH_SENS)
      touchY = ny; touchX = nx
    }
    const onTouchEnd = () => { touchActive = false; scheduleIdleSnap() }

    const onMouseMove = (e: MouseEvent) => {
      // Normalized −1..+1 across viewport — drives parallax.
      mouse.current.x = (e.clientX / window.innerWidth)  * 2 - 1
      mouse.current.y = (e.clientY / window.innerHeight) * 2 - 1
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') snapToRoom(Math.round(scrollTarget.current) + 1)
      if (e.key === 'ArrowUp'   || e.key === 'ArrowLeft')  snapToRoom(Math.round(scrollTarget.current) - 1)
    }

    window.addEventListener('wheel',      onWheel,      { passive: false })
    window.addEventListener('touchstart', onTouchStart, { passive: true })
    window.addEventListener('touchmove',  onTouchMove,  { passive: false })
    window.addEventListener('touchend',   onTouchEnd,   { passive: true })
    window.addEventListener('mousemove',  onMouseMove)
    window.addEventListener('keydown',    onKey)
    return () => {
      window.removeEventListener('wheel',      onWheel)
      window.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('touchmove',  onTouchMove)
      window.removeEventListener('touchend',   onTouchEnd)
      window.removeEventListener('mousemove',  onMouseMove)
      window.removeEventListener('keydown',    onKey)
    }
  }, [setTarget, scheduleIdleSnap, snapToRoom])

  useEffect(() => {
    const id = setTimeout(() => setShowSkip(true), 1500)
    return () => clearTimeout(id)
  }, [])

  const handleEnter = useCallback(() => { window.location.href = 'https://dockaan.com/' }, [])
  const handleSkip  = useCallback(() => navigate('/browse'), [navigate])
  const sc = SCENES[activeScene]

  return (
    <>
      {/* Three.js canvas container */}
      <div ref={mountRef} className="intro-mount" />

      {/* Header */}
      <div className="intro-fixed-ui">
        <div className="intro-logo-wrap">
          <img src="/logo.png" alt="Dukkan" className="intro-logo" />
          <span className="intro-logo-text">Dukkan</span>
        </div>
        {activeScene === 0 && (
          <div className="intro-scroll-hint-box">
            <div className="intro-scroll-mouse"><div className="intro-scroll-wheel" /></div>
            <span>Scroll to explore</span>
          </div>
        )}
        <div className="intro-controls">
          {showSkip && (
            <button className="intro-skip-btn" onClick={handleSkip}>Skip ↗</button>
          )}
        </div>
      </div>

      {/* Right progress dots */}
      <div className="intro-progress-dots">
        {SCENES.map((s, i) => (
          <div
            key={s.id}
            className={`intro-dot${i === activeScene ? ' intro-dot--active' : ''}${i < activeScene ? ' intro-dot--done' : ''}`}
            style={{ '--accent': s.accentColor } as React.CSSProperties}
            onClick={() => snapToRoom(i)}
          />
        ))}
      </div>

      {/* Bottom scene info */}
      {!showFinal && (
        <div
          className="intro-store-label"
          key={activeScene}
          style={{ '--accent': sc.accentColor } as React.CSSProperties}
        >
          <div className="intro-label-accent-line" />
          <div className="intro-label-body">
            <div className="intro-label-top">
              <span className="intro-label-index">
                {String(activeScene + 1).padStart(2, '0')}&thinsp;—&thinsp;{String(N).padStart(2, '0')}
              </span>
              <span className="intro-label-emoji">{sc.emoji}</span>
            </div>
            <span className="intro-label-name">{sc.name}</span>
            <span className="intro-label-tagline">{sc.tagline}</span>
            <span className="intro-label-desc">{sc.description}</span>
          </div>
        </div>
      )}

      {/* Scene counter */}
      <div className="intro-counter">
        <span className="intro-counter-current">{String(activeScene + 1).padStart(2, '0')}</span>
        <span className="intro-counter-sep"> / </span>
        <span className="intro-counter-total">{String(N).padStart(2, '0')}</span>
      </div>

      {/* Final CTA */}
      {showFinal && (
        <div className="intro-final-overlay">
          <img src="/logo.png" alt="Dukkan" className="intro-final-logo" />
          <h1 className="intro-final-title">Every Store.<br /><em>Right Here.</em></h1>
          <p className="intro-final-sub">Walk through 3D-scanned stores from your phone.</p>
          <button className="intro-enter-btn" style={{ '--accent': sc.accentColor } as React.CSSProperties}
            onClick={handleEnter}>
            <span>Start Exploring</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
          <button className="intro-final-skip" onClick={handleSkip}>Already know Dukkan? Browse →</button>
        </div>
      )}


      {/* Progress bar */}
      <div className="intro-progress-bar">
        <div className="intro-progress-fill"
          style={{ width: `${(activeScene / (N - 1)) * 100}%`, background: sc.accentColor }} />
      </div>
    </>
  )
}
